"use strict";

const WebPush = {
    options: {
        application_server_key: null,
        service_worker_url: null,
        debug: true,
        session_key: 'web-push-session-key',
        session_value: 'web-push-service-worker-registered',
        button_enable_id: 'web-push-button-enable',
        button_disable_id: 'web-push-button-disable',
    },
    state: 'unknown',
    button_enable: null,
    button_disable: null,
};

WebPush.init = function (userOptions) {
    this.button_enable = document.getElementById(this.options.button_enable_id);
    this.button_disable = document.getElementById(this.options.button_disable_id);
    this.changeState('unknown');

    for (const prop in userOptions) {
        if (Object.prototype.hasOwnProperty.call(this.options, prop)) {
            this.options[prop] = userOptions[prop];
        }
    }

    if (!this.options.application_server_key) {
        this.logWarning("No application server key provided.");
        return;
    }

    if (!this.checkBrowserSupport()){
        this.changeState('incompatible');
        return;
    }

    this.checkPermissions();

    if (this.button_enable) {
        this.button_enable.addEventListener('click', this.enableClick.bind(this));
    }
    if (this.button_disable) {
        this.button_disable.addEventListener('click', this.disableClick.bind(this));
    }

    //if (sessionStorage.getItem(this.options.session_key) !== this.options.session_value) {
        this.registerServiceWorker();
    //}
};

WebPush.registerServiceWorker = function () {
    navigator.serviceWorker.register(this.options.service_worker_url, { updateViaCache: 'none' }).then(
        () => {
            this.sendSubscriptionToServer();
        },
        e => {
            this.logWarning('Service worker registration failed', e);
        }
    );
}

WebPush.sendSubscriptionToServer = function () {
    navigator.serviceWorker.ready
        .then(serviceWorkerRegistration => serviceWorkerRegistration.pushManager.getSubscription())
        .then(subscription => {
            if (!subscription) {
                this.logWarning("Push notifications are denied by the user (no subscription)");
                this.changeState('disabled');
                return;
            }
            if (sessionStorage.getItem(this.options.session_key) === this.options.session_value) {
                return subscription;
            }
            return this.updateSubscription(subscription);
        })
        .catch(e => {
            this.logWarning('Error when updating the subscription', e);
        });
}

WebPush.checkBrowserSupport = function () {
    if (!('serviceWorker' in navigator)) {
        this.logWarning("Service workers are not supported in this browser.");
        return false;
    }

    if (!('PushManager' in window)) {
        this.logWarning("Push notifications are not supported in this browser.");
        return false;
    }

    if (!('showNotification' in ServiceWorkerRegistration.prototype)) {
        this.logWarning("Push notifications are not supported by this browser");
        return false;
    }

    return true;
}

WebPush.checkPermissions = function () {
    switch (Notification.permission) {
        case 'granted':
            this.changeState('enabled');
            break;
        case 'denied':
            this.changeState('disabled');
            break;
        default:
            this.changeState('disabled');
    }
}

WebPush.changeState = function (state) {
    this.state = state;
    switch (state) {
        case 'enabled':
            this.button_enable?.setAttribute('disabled', '');
            this.button_disable?.removeAttribute('disabled');
            break;
        case 'disabled':
            this.button_enable?.removeAttribute('disabled');
            this.button_disable?.setAttribute('disabled', '');
            break;
        default:
            this.button_enable?.setAttribute('disabled', '');
            this.button_disable?.setAttribute('disabled', '');
    }
}

WebPush.enableClick = function (event) {
    event.preventDefault();
    this.changeState('unknown');

    new Promise((resolve, reject) => {
        switch (Notification.permission) {
            case 'granted':
                return resolve();
            case 'denied':
                return reject(new Error('Push messages are blocked.'));
            case "default":
                return Notification.requestPermission().then(result => {
                    if (result !== 'granted') {
                        reject(new Error('Bad permission result'));
                    } else {
                        resolve();
                    }
                });
            default:
                return reject(new Error('Unknown permission'));
        }
    })
        .then(() => navigator.serviceWorker.ready)
        .then(serviceWorkerRegistration =>
            serviceWorkerRegistration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: this.urlBase64ToUint8Array(
                    this.options.application_server_key
                ),
            })
        )
        .then(subscription => {
            const inviteToken = this.button_enable.dataset.inviteToken;
            return this.updateSubscription(subscription, inviteToken);
        })
        .then(subscription => subscription && this.changeState('enabled'))
        .catch(e => {
            this.checkPermissions();
            this.logWarning('Error when subscribing the user', e);
        });
}

WebPush.disableClick = function (event) {
    event.preventDefault();
    this.changeState('unknown');

    navigator.serviceWorker.ready
        .then(serviceWorkerRegistration => serviceWorkerRegistration.pushManager.getSubscription())
        .then(subscription => {
            if (!subscription) {
                this.changeState('disabled');
                return;
            }

            return this.deleteSubscription(subscription);
        })
        .then(subscription => {
            this.logWarning("Unsubscribe");
            return subscription.unsubscribe();
        })
        .then(() => this.changeState('disabled'))
        .catch(e => {
            this.logWarning('Error when unsubscribing the user', e);
            this.changeState('disabled');
        });
}

WebPush.updateSubscription = function (subscription, inviteToken = null) {
    const input_url = inviteToken ? '/web-push/update?invite_token=' + inviteToken : '/web-push/update';
    return fetch(input_url, {
        method: 'POST',
        body: this.dataFromSubscription(subscription),
    })
        .then((response) => {
            if (response.ok) {
                sessionStorage.setItem(this.options.session_key, this.options.session_value);
            }
        })
        .then(() => subscription);
}

WebPush.deleteSubscription = function (subscription) {
    return fetch('/web-push/delete', {
        method: 'POST',
        body: this.dataFromSubscription(subscription),
    }).then(() => subscription);
}

WebPush.logWarning = function (message, ...data) {
    if (this.options.debug) console.warn("WebPush: " + message, ...data);
}

WebPush.dataFromSubscription = function (subscription) {
    return JSON.stringify(subscription.toJSON());
}

WebPush.urlBase64ToUint8Array = function (base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export {WebPush};